import { Dispatch, SetStateAction, useMemo } from 'react'
import { percentageBoundary } from './PercentageBoundary'
import styles from './PercentInput.module.css'
import { ProjectAttributes } from '../../../../interfaces/project/ProjectAttributes'
import { useInteractionContext } from '../../../../context/InteractionContext'
import { useMemorizedEditing } from '../../customHooks/useMemorizedEditing'

interface PercentInputProps {
  values: ProjectAttributes
  initialValues: ProjectAttributes
  label: string
  setValues: Dispatch<SetStateAction<ProjectAttributes>>
  setChangedValue: Dispatch<
    SetStateAction<{ [key: string]: string | number | null }>
  >
  attributeName: string
  disabled: boolean
  limit?: number | null
}

// Function to check if a label should be treated as multiline
const isMultilineLabel = (label: string) => {
  return label?.length > 20
}

// Function to handle long labels, formatting them for better display
const formatLongLabel = (label: string) => {
  return label || ''
}

export const PercentInput = ({
  values,
  initialValues,
  setValues,
  setChangedValue,
  label,
  attributeName,
  limit,
  disabled,
}: PercentInputProps) => {
  const { refreshLoading } = useInteractionContext()

  const { setInputBlurEvent, setKeyTriggerEvent, handleChangedPercent } =
    useMemorizedEditing({
      values,
      initialValues,
      setValues,
      setChangedValue,
      attributeName,
      regExp: /^(100(\.00?)?|\d{1,2}(\.\d{1,2})?|0)$/,
    })

  const inputValue = values?.[attributeName as keyof ProjectAttributes] ?? ''

  const percentInput = () => (
    <input
      data-testid={attributeName}
      className={styles.percentInput}
      value={inputValue}
      type="number"
      step="0.01"
      min="0"
      max="100"
      disabled={disabled}
      tabIndex={0}
      onChange={(e) => handleChangedPercent(e)}
      onKeyDown={(e) => setKeyTriggerEvent(e)}
      onBlur={(e) => setInputBlurEvent(e)}
    />
  )

  const disabledPercentInput = () => (
    <input
      data-testid={attributeName}
      className={styles.percentInput}
      value={inputValue}
      type="number"
      disabled
    />
  )

  // Format the label for display
  const formattedLabel = useMemo(() => formatLongLabel(label), [label])
  const isMultiline = useMemo(() => isMultilineLabel(label), [label])

  const inputClassName = percentageBoundary(Number(inputValue), limit)

  return (
    <div className={styles.percentInputContainer}>
      <label
        className={`${styles.percentInputLabel} ${
          isMultiline ? styles.multilineLabel : ''
        }`}
      >
        {formattedLabel}
      </label>
      <span className={inputClassName}>
        {refreshLoading ? disabledPercentInput() : percentInput()}
      </span>
    </div>
  )
}
